import React, { useState } from 'react';
import { formatAmount, varianceTone } from '../../utils/statementFormat';

/**
 * Renders a statement or breakdown result: label column plus value columns,
 * subtotal and total styling, expandable account lines, favourable /
 * adverse variance colouring and warnings. compact hides drill-down and
 * secondary columns for wall mode.
 */
const StatementView = ({ result, compact = false, maxColumns = null }) => {
  const [open, setOpen] = useState(() => new Set());
  if (!result) return null;

  const columns = maxColumns ? result.columns.slice(0, maxColumns) : result.columns;
  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const cell = (row, c, child = false) => {
    const value = row.values?.[c.key];
    const tone = c.variance ? varianceTone(value, row.direction) : null;
    const alert = c.alert && value > 0;
    return (
      <td
        key={c.key}
        className={['statement-num', tone ? `statement-${tone}` : '', alert ? 'statement-alert' : '', c.emphasis ? 'statement-emphasis' : '', child ? 'statement-child-num' : ''].filter(Boolean).join(' ')}
      >
        {formatAmount(value, c.format)}
      </td>
    );
  };

  return (
    <div className={`statement ${compact ? 'statement--compact' : ''}`}>
      <div className="statement-scroll">
        <table className="statement-table">
          <thead>
            <tr>
              <th className="statement-label-head" scope="col">{result.period?.label || ''}</th>
              {columns.map((c) => (
                <th key={c.key} scope="col" className="statement-num" title={c.rangeLabel || undefined}>
                  {c.title}
                  {c.rangeLabel && !compact && <span className="statement-range">{c.rangeLabel}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => {
              const expandable = !compact && row.children && row.children.length > 0;
              const isOpen = open.has(row.key);
              if (row.style === 'heading') {
                return <tr key={row.key} className="statement-row statement-row--heading"><th scope="row" colSpan={columns.length + 1}>{row.label}</th></tr>;
              }
              return (
                <React.Fragment key={row.key}>
                  <tr className={`statement-row statement-row--${row.style || 'line'}`}>
                    <th scope="row" className="statement-label">
                      {expandable ? (
                        <button type="button" className="statement-expand" aria-expanded={isOpen} onClick={() => toggle(row.key)}>
                          <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span> {row.label}
                        </button>
                      ) : row.label}
                    </th>
                    {columns.map((c) => cell(row, c))}
                  </tr>
                  {expandable && isOpen && row.children.map((child) => (
                    <tr key={`${row.key}-${child.label}`} className="statement-row statement-row--child">
                      <th scope="row" className="statement-label statement-child-label">{child.label}</th>
                      {columns.map((c) => cell({ ...child, direction: row.direction }, c, true))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {!compact && result.warnings && result.warnings.length > 0 && (
        <ul className="statement-warnings" role="note">
          {result.warnings.map((w) => <li key={w}>⚠ {w}</li>)}
        </ul>
      )}
    </div>
  );
};

export default StatementView;
